<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import {
		AdvancedCard,
		Alert,
		Button,
		ButtonV2,
		CardGrid,
		Icon,
		Input,
		InputV2,
		Loader,
		Modal,
		SelectCard,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
		toast,
	} from '@Shelly/shelly-design-system';
	import {
		addUnit,
		getUnitsInfo,
		receiveUnit,
		setHomeAvatar,
		setUnitName,
	} from 'src/services/iotu/house-management';
	import { isMultiUnitResult, switchUnit } from 'src/services/auth_v2';
	import { currentUnitId, refreshHomes } from 'src/stores/account/home';
	import { pendingUnitTransfers } from 'src/stores/account/pending_unit_transfers';
	import { loginRequest } from 'src/services/iotu/v2_users_auth';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { migrateIcodesOnUnitChange } from 'src/helpers/installer_helpers';
	import { profiles } from 'src/stores/features/profiles';
	import { purgeLocalLoggedInProfile } from 'src/components/Profiles/profiles_helpers';
	import type { DropdownMenuDataType } from 'src/components/Profiles/DropdownItem.types';
	import type { AvailableUnits } from 'src/types/responses/usersAuthResponse';
	import { MAX_OWNED_HOMES } from 'src/constants';
	import {
		buildPendingHome,
		canAddOwnedHome,
		getHomeDisplayName,
		getProfileDisplayName,
		getProfileEmailSubtitle,
		isUnnamedDefaultHome,
		resolveUniqueHomeName,
	} from 'src/helpers/houses/homes_helpers';
	import AddHomeManagementPreview from './AddHomeManagementPreview.svelte';
	import AvatarPicker from 'src/components/Avatar/AvatarPicker.svelte';
	import HomeAvatar from 'src/components/Homes/HomeAvatar.svelte';
	import { escapeHtml } from './home-management-utils';
	import { normalizeEmail } from 'src/helpers/email_helpers';
	import type { HomeAvatar as HomeAvatarType } from 'src/types/Home';

	const highlightLabel = (text: string): string => {
		if (!text) return '';
		return `<span class="text-primary">${escapeHtml(text)}</span>`;
	};

	export let showModal = false;
	/** Design-system variant: V2 uses ButtonV2/InputV2; legacy uses Button/Input. */
	export let variant: 'v2' | 'legacy' = 'legacy';

	const dispatch = createEventDispatcher<{ close: void }>();

	type AddHomeMethod = 'create' | 'convert' | '';

	const PREVIEW_STEP_INDEX = 2;
	const DETAILS_STEP_INDEX = 1;

	let activeStepIndex = 0;
	let addHomeMethod: AddHomeMethod = '';
	let isLoading = false;

	let unitNameInput = '';
	let unitNameInputError = '';
	let unitAvatar: HomeAvatarType = {};

	let selectedReceiveProfile: DropdownMenuDataType | null = null;
	let profileUnits: AvailableUnits = {};
	let profileUnitEntries: [string, string][] = [];
	let selectedReceiveUnitId: string | null = null;
	let isLoadingUnits = false;
	let receiveError = '';
	let showReceiveAccountDeletionWarning = false;
	let confirmDeletionEmailInput = '';
	let confirmDeletionEmailError = '';
	let showSwitchToNewHomePrompt = false;
	let addedHomeId: string | null = null;
	let addedHomeName = '';
	let previewExistingHomes: [string, string][] = [];

	$: units = $user_data.available_units ?? {};
	$: unitEntries = Object.entries(units) as [string, string][];
	$: otherProfiles = $profiles.filter((p) => !p.isSelected);
	$: hasOtherLoggedInProfiles = otherProfiles.length > 0;
	$: incomingPendingCount = ($pendingUnitTransfers.incoming ?? []).length;
	$: canAddAnyHome = canAddOwnedHome(units, incomingPendingCount);
	$: isV2 = variant === 'v2';
	$: trimmedUnitName = unitNameInput.trim();
	$: defaultHomeLabel = $format('settings_default_home_name', { default: 'My Home' });
	$: sourceAccountEmail = selectedReceiveProfile?.email ?? '';
	$: currentAccountEmail = $user_data.email;
	$: selectedReceiveRawName =
		profileUnitEntries.find(([unitId]) => unitId === selectedReceiveUnitId)?.[1] ?? '';
	$: shouldInheritProfileName =
		addHomeMethod === 'convert' &&
		profileUnitEntries.length === 1 &&
		isUnnamedDefaultHome(selectedReceiveRawName, defaultHomeLabel, selectedReceiveUnitId);
	$: selectedReceiveUnitName = shouldInheritProfileName
		? resolveUniqueHomeName(
				getProfileDisplayName(
					selectedReceiveProfile?.title,
					sourceAccountEmail,
					selectedReceiveUnitId,
				),
				Object.values(units),
			)
		: getHomeDisplayName(selectedReceiveRawName, sourceAccountEmail);
	$: showHomePicker = profileUnitEntries.length > 1;
	$: createPreviewHome = buildPendingHome(trimmedUnitName || defaultHomeLabel, unitAvatar);

	$: isCreateStepComplete = addHomeMethod === 'create' && trimmedUnitName.length > 0;
	$: isConvertStepComplete =
		addHomeMethod === 'convert' && !!selectedReceiveProfile && !!selectedReceiveUnitId;
	$: isDetailsStepComplete = isCreateStepComplete || isConvertStepComplete;
	$: willDeleteSourceAccount =
		addHomeMethod === 'convert' && !!selectedReceiveProfile && profileUnitEntries.length === 1;
	$: isDeletionConfirmEmailValid =
		normalizeEmail(confirmDeletionEmailInput) === normalizeEmail(sourceAccountEmail);
	$: singleHomeDeletionStepWarning = $format('settings_add_home_convert_single_home_warning', {
		values: {
			home: highlightLabel(selectedReceiveUnitName),
			sourceEmail: highlightLabel(sourceAccountEmail),
		},
		default:
			'{home} is the only home on {sourceEmail}. Adding it to your account will permanently delete the source account.',
	});
	$: singleHomeDeletionPreviewWarning = $format(
		'settings_add_home_convert_single_home_preview_warning',
		{
			values: {
				home: highlightLabel(selectedReceiveUnitName),
				sourceEmail: highlightLabel(sourceAccountEmail),
			},
			default:
				'You are about to add {home} to your account and permanently delete {sourceEmail}. This cannot be undone.',
		},
	);

	$: detailsStepTitle =
		addHomeMethod === 'convert'
			? $format('wizard_step_select_home', { default: 'Select home' })
			: $format('wizard_step_home_setup', { default: 'Home setup' });

	$: wizardSteps = [
		{
			title: $format('wizard_step_choose_method', { default: 'Choose method' }),
		},
		{
			title: detailsStepTitle,
		},
		{
			title: $format('wizard_step_preview', { default: 'Preview' }),
		},
	];

	const applyIcodesMigration = (previousUnitCount: number): void => {
		const currentUnitCount = Object.keys($user_data.available_units ?? {}).length;
		const nextIcodes = migrateIcodesOnUnitChange(
			$user_settings?.icodes,
			String($user_data?.user_id),
			previousUnitCount,
			currentUnitCount,
		);
		if (nextIcodes !== null) {
			user_settings.set({ ...$user_settings, icodes: nextIcodes });
		}
	};

	const resetWizardState = (): void => {
		activeStepIndex = 0;
		addHomeMethod = '';
		unitNameInput = '';
		unitNameInputError = '';
		unitAvatar = {};
		selectedReceiveProfile = null;
		profileUnits = {};
		profileUnitEntries = [];
		selectedReceiveUnitId = null;
		isLoadingUnits = false;
		receiveError = '';
		showReceiveAccountDeletionWarning = false;
		confirmDeletionEmailInput = '';
		confirmDeletionEmailError = '';
		showSwitchToNewHomePrompt = false;
		addedHomeId = null;
		addedHomeName = '';
		previewExistingHomes = [];
	};

	const finishAddHomeFlow = (): void => {
		resetWizardState();
		dispatch('close');
	};

	const promptSwitchToNewHome = (unitId: string, homeName: string): void => {
		if (get(currentUnitId) === unitId) {
			toast.open({
				type: 'success',
				title:
					addHomeMethod === 'create'
						? $format('success_add_unit', { default: 'New home added successfully' })
						: $format('success_receive_unit', {
								default: 'Home received successfully',
							}),
			});
			closeModal();
			return;
		}

		addedHomeId = unitId;
		addedHomeName = homeName;
		showModal = false;
		showSwitchToNewHomePrompt = true;
	};

	const handleStayOnCurrentHome = (): void => {
		toast.open({
			type: 'success',
			title:
				addHomeMethod === 'create'
					? $format('success_add_unit', { default: 'New home added successfully' })
					: $format('success_receive_unit', {
							default: 'Home received successfully',
						}),
		});
		finishAddHomeFlow();
	};

	const handleSwitchToNewHome = async (): Promise<void> => {
		if (!addedHomeId) {
			finishAddHomeFlow();
			return;
		}

		isLoading = true;

		try {
			await switchUnit(addedHomeId);

			toast.open({
				type: 'success',
				title: $format('settings_add_home_switched_success', {
					values: { name: addedHomeName },
					default: 'Switched to "{name}".',
				}),
			});
			finishAddHomeFlow();
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_switch_unit_failed', {
					default: 'Could not switch to this home. Please try again.',
				}),
			});
		} finally {
			isLoading = false;
		}
	};

	const closeModal = (): void => {
		showModal = false;
		resetWizardState();
		dispatch('close');
	};

	const closeReceiveAccountDeletionWarning = (): void => {
		showReceiveAccountDeletionWarning = false;
		confirmDeletionEmailInput = '';
		confirmDeletionEmailError = '';
		showModal = true;
	};

	const openReceiveAccountDeletionWarning = (): void => {
		confirmDeletionEmailInput = '';
		confirmDeletionEmailError = '';
		showModal = false;
		showReceiveAccountDeletionWarning = true;
	};

	const selectAddHomeMethod = (method: AddHomeMethod): void => {
		if (!canAddAnyHome) return;
		if (method === 'convert' && !hasOtherLoggedInProfiles) return;
		addHomeMethod = method;
	};

	const goToDetailsStep = (): void => {
		if (!canAddAnyHome) return;

		if (!addHomeMethod) {
			toast.open({
				type: 'warning',
				title: $format('settings_add_home_method_required', {
					default: 'Please choose how you would like to add a home.',
				}),
				duration: 2000,
			});
			return;
		}
		activeStepIndex = DETAILS_STEP_INDEX;
	};

	const goToPreviewStep = (): void => {
		if (addHomeMethod === 'create') {
			if (!trimmedUnitName) {
				unitNameInputError = $format('error_required_field', {
					default: 'This field is required',
				});
				return;
			}
			unitNameInputError = '';
		} else if (addHomeMethod === 'convert') {
			if (!selectedReceiveProfile || !selectedReceiveUnitId) {
				toast.open({
					type: 'warning',
					title: $format('settings_add_home_select_home_required', {
						default: 'Please select a profile and a home.',
					}),
					duration: 2000,
				});
				return;
			}
		}

		previewExistingHomes = unitEntries;
		activeStepIndex = PREVIEW_STEP_INDEX;
	};

	const canNavigateToStep = (stepIndex: number): boolean => {
		if (stepIndex === 0) return true;
		if (stepIndex === DETAILS_STEP_INDEX) return !!addHomeMethod;
		if (stepIndex === PREVIEW_STEP_INDEX) return isDetailsStepComplete;
		return false;
	};

	const navigateToStep = (stepIndex: number): void => {
		if (!canNavigateToStep(stepIndex)) return;
		if (stepIndex === PREVIEW_STEP_INDEX) {
			previewExistingHomes = unitEntries;
		}
		activeStepIndex = stepIndex;
	};

	const saveAvatarOnUnit = async (
		unitId: string,
		avatar: HomeAvatarType,
	): Promise<{ avatarSaved: boolean; restoredPreviousUnit: boolean }> => {
		if (!avatar?.image && !avatar?.backgroundColor && !avatar?.text) {
			return { avatarSaved: true, restoredPreviousUnit: true };
		}

		const previousUnitId = get(currentUnitId);
		const shouldRestorePreviousUnit = Boolean(previousUnitId && previousUnitId !== unitId);

		let avatarSaved = false;
		let restoredPreviousUnit = !shouldRestorePreviousUnit;

		try {
			await switchUnit(unitId);

			const res = await setHomeAvatar(avatar);
			avatarSaved = res?.isok !== false;
		} catch {
			avatarSaved = false;
		}

		if (shouldRestorePreviousUnit && get(currentUnitId) === unitId) {
			try {
				await switchUnit(previousUnitId);
				restoredPreviousUnit = true;
			} catch {
				restoredPreviousUnit = false;
			}
		}

		return { avatarSaved, restoredPreviousUnit };
	};

	const confirmCreateHome = async (): Promise<void> => {
		if (!trimmedUnitName || !canAddAnyHome) return;

		isLoading = true;

		try {
			const prevCount = unitEntries.length;
			const prevUnitIds = new Set(Object.keys(units));
			const result = await addUnit(trimmedUnitName, $user_data.lang, $user_data.timezone);

			if (result?.isok === false) {
				throw new Error($format('error_add_unit', { default: 'Failed to add new home' }));
			}

			await refreshHomes();
			applyIcodesMigration(prevCount);

			const newUnitId =
				Object.keys($user_data.available_units ?? {}).find((id) => !prevUnitIds.has(id)) ?? null;

			if (newUnitId) {
				let restoredPreviousUnit = true;

				if (unitAvatar?.image || unitAvatar?.backgroundColor || unitAvatar?.text) {
					const avatarResult = await saveAvatarOnUnit(newUnitId, unitAvatar);
					restoredPreviousUnit = avatarResult.restoredPreviousUnit;
					if (!avatarResult.avatarSaved) {
						toast.open({
							type: 'warning',
							title: $format('error_save_home_avatar', {
								default: 'Home was created, but the avatar could not be saved.',
							}),
						});
					}
				}

				if (restoredPreviousUnit) {
					promptSwitchToNewHome(newUnitId, trimmedUnitName);
				} else {
					toast.open({
						type: 'warning',
						title: $format('settings_add_home_return_failed', {
							values: { name: trimmedUnitName },
							default:
								'"{name}" was created, but we couldn\'t switch back to your previous home. You are now on "{name}".',
						}),
					});
					closeModal();
				}
			} else {
				toast.open({
					type: 'success',
					title: $format('success_add_unit', { default: 'New home added successfully' }),
				});
				closeModal();
			}
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_add_unit', { default: 'Failed to add new home' }),
			});
		} finally {
			isLoading = false;
		}
	};

	const onSelectReceiveProfile = async (profile: DropdownMenuDataType): Promise<void> => {
		if (selectedReceiveProfile?.email === profile.email) return;

		selectedReceiveProfile = profile;
		selectedReceiveUnitId = null;
		profileUnits = {};
		profileUnitEntries = [];
		receiveError = '';
		confirmDeletionEmailInput = '';
		confirmDeletionEmailError = '';
		isLoadingUnits = true;

		try {
			const result = await loginRequest(profile.email ?? '', profile.pass ?? '');

			if (isMultiUnitResult(result)) {
				// Multi-home accounts return real home names in the login response.
				profileUnits = result.availableUnits;
			} else {
				// Single-home logins don't carry the home name, so ask the source
				// account directly to preserve a user-given name and to detect a
				// genuinely unnamed home (rather than falling back to its unit_id).
				const uid = String(result.data.user_id ?? '');
				let homeName = '';

				if (uid && result.data.token && result.data.user_api_url) {
					try {
						const info = await getUnitsInfo({
							token: result.data.token,
							userApiUrl: result.data.user_api_url,
						});
						const ownUnits = info?.data?.own_units ?? [];
						const match = ownUnits.find((u) => String(u.unit_id) === uid) ?? ownUnits[0];
						homeName = match?.name?.trim() ?? '';
					} catch {
						homeName = '';
					}
				}

				profileUnits = uid ? { [uid]: homeName } : {};
			}

			profileUnitEntries = Object.entries(profileUnits) as [string, string][];

			if (profileUnitEntries.length === 1) {
				selectedReceiveUnitId = profileUnitEntries[0][0];
			}

			if (profileUnitEntries.length === 0) {
				receiveError = $format('error_no_units', {
					default: 'This account has no available homes.',
				});
			}
		} catch {
			receiveError = $format('error_fetch_units', {
				default: 'Failed to retrieve homes for this account.',
			});
			profileUnits = {};
			profileUnitEntries = [];
		} finally {
			isLoadingUnits = false;
		}
	};

	const handleReceiveSubmit = (): void => {
		if (!selectedReceiveProfile || !selectedReceiveUnitId) return;

		if (profileUnitEntries.length === 1) {
			openReceiveAccountDeletionWarning();
		} else {
			showModal = false;
			executeReceive();
		}
	};

	const handleReceiveAccountDeletionConfirmed = (): void => {
		if (!isDeletionConfirmEmailValid) {
			confirmDeletionEmailError = $format('settings_add_home_confirm_email_mismatch', {
				values: { email: sourceAccountEmail },
				default: 'Please type "{email}" exactly to confirm.',
			});
			return;
		}

		confirmDeletionEmailError = '';
		showReceiveAccountDeletionWarning = false;
		executeReceive();
	};

	const confirmAddHome = (): void => {
		if (addHomeMethod === 'create') {
			confirmCreateHome();
			return;
		}
		handleReceiveSubmit();
	};

	const executeReceive = async (): Promise<void> => {
		if (!selectedReceiveProfile || !selectedReceiveUnitId || !canAddAnyHome) return;

		isLoading = true;

		try {
			const prevCount = unitEntries.length;
			const receivedUnitId = selectedReceiveUnitId;
			const profile = selectedReceiveProfile;
			const purgeSourceProfile = profileUnitEntries.length === 1;
			const sourceProfileEmail = profile?.email ?? '';
			const inheritProfileName = shouldInheritProfileName;
			let receivedHomeName = selectedReceiveUnitName;
			const result = await receiveUnit(
				Number(selectedReceiveUnitId),
				profile.email ?? '',
				profile.pass ?? '',
			);

			if (!result.isok) {
				throw new Error($format('error_receive_unit', { default: 'Failed to receive home' }));
			}

			// Single unnamed source home: persist the inherited profile name so the new
			// home doesn't fall back to its raw unit_id on the cloud.
			if (inheritProfileName && receivedUnitId && receivedHomeName.trim()) {
				await setUnitName(Number(receivedUnitId), receivedHomeName);
			}

			await refreshHomes();
			applyIcodesMigration(prevCount);

			if (purgeSourceProfile && sourceProfileEmail) {
				purgeLocalLoggedInProfile(sourceProfileEmail);
			}

			if (receivedUnitId) {
				promptSwitchToNewHome(receivedUnitId, receivedHomeName);
			} else {
				toast.open({
					type: 'success',
					title: $format('success_receive_unit', {
						default: 'Home received successfully',
					}),
				});
				resetWizardState();
			}
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title:
					err?.message ?? $format('error_receive_unit', { default: 'Failed to receive home' }),
			});
		} finally {
			isLoading = false;
			selectedReceiveProfile = null;
			selectedReceiveUnitId = null;
			profileUnits = {};
			profileUnitEntries = [];
		}
	};
</script>

<Modal
	{showModal}
	on:close={closeModal}
	title={$format('settings_add_home_wizard_title', { default: 'Add home' })}
	removeScrollPadding
>
	<Wizard
		slot="toolbar"
		activeStepNumber={activeStepIndex}
		stepsTotal={wizardSteps.length}
		activeStepTitle={wizardSteps[activeStepIndex]?.title ?? ''}
	>
		{#each wizardSteps as step, i (i)}
			<WizardItem
				active={activeStepIndex === i}
				title={step.title}
				disabled={activeStepIndex !== i && !canNavigateToStep(i)}
				on:click={() => navigateToStep(i)}
			/>
		{/each}
	</Wizard>

	<div class:flex-overflow-hidden={false} slot="content">
		<WizardContainer {activeStepIndex} adaptiveHeight>
			<WizardContent active={activeStepIndex === 0} class="pl-0 pr-0 pb-s pt-s">
				<div class="pl-m pr-m">
					<Text
						element={isV2 ? undefined : 'p'}
						styleType={isV2 ? 'body-small' : undefined}
						class="mb-m"
					>
						{$format('settings_add_home_wizard_step_choose_method', {
							default: 'How would you like to add a home?',
						})}
					</Text>

					{#if !canAddAnyHome}
						<Alert
							type="warning"
							class="mb-m"
							heading={$format('alert_owned_homes_limit_reached', {
								values: { limit: MAX_OWNED_HOMES },
								default: `You can create up to ${MAX_OWNED_HOMES} homes. Delete an existing home to add a new one.`,
							})}
						/>
					{/if}

					<CardGrid widthUnit="%" sm={100} md={100} noPaddingBottom>
						<SelectCard
							cardBgColor={isV2 ? 'color-1' : undefined}
							class="width-full"
							type="horizontal"
							variant="radio"
							icon="fal fa-house"
							title={$format('settings_add_home_option_create', {
								default: 'Create new home',
							})}
							descriptiveText={$format('settings_add_home_option_create_description', {
								default: 'Add a new home to your account.',
							})}
							selected={addHomeMethod === 'create'}
							disabled={!canAddAnyHome}
							on:click={() => selectAddHomeMethod('create')}
						/>
						<!-- <SelectCard
							cardBgColor={isV2 ? 'color-1' : undefined}
							class="width-full mt-s"
							type="horizontal"
							variant="radio"
							icon="fal fa-arrow-right-arrow-left"
							title={$format('settings_add_home_option_convert_title', {
								default: 'From another account',
							})}
							descriptiveText={$format('settings_add_home_option_convert_description', {
								default:
									'Convert an existing account to a home or choose a home from another account',
							})}
							selected={addHomeMethod === 'convert'}
							disabled={!canAddAnyHome || !hasOtherLoggedInProfiles}
							on:click={() => selectAddHomeMethod('convert')}
						/> -->
					</CardGrid>
				</div>
			</WizardContent>

			<WizardContent active={activeStepIndex === 1} class="pl-0 pr-0 pb-s pt-s">
				<div class="pl-m pr-m">
					{#if addHomeMethod === 'create'}
						<form autocomplete="off" novalidate class="width-full" on:submit|preventDefault>
							<div class="d-flex justify-content-center mb-m">
								<HomeAvatar home={createPreviewHome} size="xl" />
							</div>

							{#if isV2}
								<InputV2
									id="add-home-name-input"
									label={$format('input_label_home_name', { default: 'Home Name' })}
									aria-labelledby="add-home-name-input"
									autocomplete="off"
									icon={null}
									bind:value={unitNameInput}
									error={unitNameInputError}
								/>
							{:else}
								<Input
									id="add-home-name-input"
									label={$format('input_label_home_name', { default: 'Home Name' })}
									aria-label={$format('input_label_home_name', {
										default: 'Home Name',
									})}
									autocomplete="off"
									icon={null}
									bind:value={unitNameInput}
									type="text"
									error={unitNameInputError}
									placeholder={$format('input_placeholder_home_name_example', {
										default: 'e.g. My Home',
									})}
								/>
							{/if}

							<hr class="mt-l mb-l" />

							<Text
								element={isV2 ? undefined : 'h2'}
								styleType={isV2 ? 'h2' : undefined}
								class="mb-m"
							>
								{$format('label_select_avatar', { default: 'Select an avatar' })}
							</Text>
							<AvatarPicker bind:avatar={unitAvatar} uploadShape="rectangular" />
						</form>
					{:else if addHomeMethod === 'convert'}
						<Text
							element={isV2 ? undefined : 'p'}
							styleType={isV2 ? 'body-small' : undefined}
							class="mb-m"
						>
							{$format('settings_receive_unit_modal_info', {
								default:
									'Select a profile and then choose the home you want to receive.',
							})}
						</Text>

						{#if otherProfiles.length > 0}
							<Text element="h4" class="mb-xs">
								{$format('settings_receive_select_profile', {
									default: 'Select owner profile',
								})}
							</Text>

							<div
								class="d-flex flex-direction-column mb-m"
								style="gap: var(--spacing-xs)"
							>
								{#each otherProfiles as profile (profile.email)}
									{@const isProfileSelected =
										selectedReceiveProfile?.email === profile.email}
									{@const profileTitle = getProfileDisplayName(
										profile.title,
										profile.email ?? '',
									)}
									{@const profileSubtitle = getProfileEmailSubtitle(
										profile.title,
										profile.email ?? '',
									)}
									{@const accAvatar = profile.acc_avatar}
									{@const useColorOnly =
										accAvatar?.backgroundColor && !accAvatar.image}
									{@const itemAvatar = {
										text: accAvatar?.text,
										icon: accAvatar?.icon,
										backgroundColor: accAvatar?.backgroundColor,
										image: useColorOnly
											? ''
											: accAvatar?.image || 'fak fa-solid-shelly',
										borderStatus: { color: 'color-white' as const },
									}}
									<AdvancedCard
										title={profileTitle}
										subtitle={{ value: profileSubtitle }}
										avatar={itemAvatar}
										selected={isProfileSelected}
										isFocusable
										aria-label={profileTitle}
										class={isV2 && !isProfileSelected ? 'bg-color-0' : ''}
										on:click={() => onSelectReceiveProfile(profile)}
									/>
								{/each}
							</div>

							{#if selectedReceiveProfile && willDeleteSourceAccount && !isLoadingUnits}
								<Alert
									type="warning"
									class="mb-m"
									heading={singleHomeDeletionStepWarning}
								/>
							{/if}
						{:else}
							<Text class="text-center">
								{$format('settings_receive_no_profiles', {
									default:
										'No other logged-in profiles available. Log in to another account first.',
								})}
							</Text>
						{/if}

						{#if isLoadingUnits}
							<div class="d-flex justify-content-center mt-m">
								<Loader size="m" />
							</div>
						{/if}

						{#if receiveError}
							<Text class="text-center mt-s" color="danger">
								{receiveError}
							</Text>
						{/if}

						{#if showHomePicker}
							<Text element="h4" class="mb-xs mt-m">
								{$format('settings_receive_select_unit', {
									default: 'Select a home to receive',
								})}
							</Text>

							<div class="d-flex flex-direction-column" style="gap: var(--spacing-xs)">
								{#each profileUnitEntries as [unitId, unitName] (unitId)}
									{@const isUnitSelected = selectedReceiveUnitId === unitId}
									{@const unitDisplayName = getHomeDisplayName(
										unitName,
										sourceAccountEmail,
									)}
									<AdvancedCard
										title={unitDisplayName}
										selected={isUnitSelected}
										isFocusable
										aria-label={unitDisplayName}
										class={isV2 && !isUnitSelected ? 'bg-color-0' : ''}
										on:click={() => (selectedReceiveUnitId = unitId)}
									>
										<Icon slot="left" icon="fal fa-house" size="l" />
									</AdvancedCard>
								{/each}
							</div>
						{/if}
					{/if}
				</div>
			</WizardContent>

			<WizardContent active={activeStepIndex === 2} class="pl-0 pr-0 pb-s pt-s">
				<div class="pl-m pr-m">
					<AddHomeManagementPreview
						pendingHomeName={addHomeMethod === 'create'
							? trimmedUnitName
							: selectedReceiveUnitName}
						pendingHomeAvatar={addHomeMethod === 'create' ? unitAvatar : undefined}
						existingHomes={previewExistingHomes}
						deletionWarningHeading={willDeleteSourceAccount
							? singleHomeDeletionPreviewWarning
							: ''}
					/>
				</div>
			</WizardContent>
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeStepIndex === 0}
			{#if isV2}
				<ButtonV2 size="l" variant="secondary" class="mr-s" on:click={closeModal}>
					{$format('button_cancel', { default: 'Cancel' })}
				</ButtonV2>
				<ButtonV2
					size="l"
					variant="primary"
					disabled={!addHomeMethod}
					on:click={goToDetailsStep}
				>
					{$format('button_next', { default: 'Next' })}
				</ButtonV2>
			{:else}
				<Button width="m" variant="secondary" class="mr-s" on:click={closeModal}>
					{$format('button_cancel', { default: 'Cancel' })}
				</Button>
				<Button width="m" variant="primary" disabled={!addHomeMethod} on:click={goToDetailsStep}>
					{$format('button_next', { default: 'Next' })}
				</Button>
			{/if}
		{:else if activeStepIndex === 1}
			{#if isV2}
				<ButtonV2 size="l" variant="secondary" class="mr-s" on:click={() => navigateToStep(0)}>
					{$format('button_back', { default: 'Back' })}
				</ButtonV2>
				<ButtonV2
					size="l"
					variant="primary"
					disabled={!isDetailsStepComplete}
					on:click={goToPreviewStep}
				>
					{$format('button_continue', { default: 'Continue' })}
				</ButtonV2>
			{:else}
				<Button width="m" variant="secondary" class="mr-s" on:click={() => navigateToStep(0)}>
					{$format('button_back', { default: 'Back' })}
				</Button>
				<Button
					width="m"
					variant="primary"
					disabled={!isDetailsStepComplete}
					on:click={goToPreviewStep}
				>
					{$format('button_continue', { default: 'Continue' })}
				</Button>
			{/if}
		{:else if isV2}
			<ButtonV2
				size="l"
				variant="secondary"
				class="mr-s"
				on:click={() => navigateToStep(DETAILS_STEP_INDEX)}
			>
				{$format('button_back', { default: 'Back' })}
			</ButtonV2>
			<ButtonV2 size="l" variant="primary" loading={isLoading} on:click={confirmAddHome}>
				{$format('button_confirm_add_unit', { default: 'Add Home' })}
			</ButtonV2>
		{:else}
			<Button
				width="m"
				variant="secondary"
				class="mr-s"
				on:click={() => navigateToStep(DETAILS_STEP_INDEX)}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
			<Button width="m" variant="primary" loading={isLoading} on:click={confirmAddHome}>
				{$format('button_confirm_add_unit', { default: 'Add Home' })}
			</Button>
		{/if}
	</div>
</Modal>

<Modal
	showModal={showSwitchToNewHomePrompt}
	on:close={handleStayOnCurrentHome}
	title={$format('settings_add_home_switch_prompt_title', {
		default: 'Switch to your new home?',
	})}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('settings_add_home_switch_prompt_info', {
				values: { name: addedHomeName },
				default: '"{name}" was added successfully. Would you like to switch to it now?',
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end flex-wrap gap-s">
		{#if isV2}
			<ButtonV2
				size="l"
				variant="secondary"
				class="width-md-m width-sm-full"
				disabled={isLoading}
				on:click={handleStayOnCurrentHome}
			>
				{$format('button_stay_on_current_home', { default: 'Stay on current home' })}
			</ButtonV2>
			<ButtonV2
				size="l"
				variant="primary"
				class="width-md-m width-sm-full"
				loading={isLoading}
				on:click={handleSwitchToNewHome}
			>
				{$format('button_switch_to_new_home', { default: 'Switch to new home' })}
			</ButtonV2>
		{:else}
			<Button
				variant="secondary"
				responsiveSize={{ sm: 'm' }}
				class="width-md-m width-sm-full"
				disabled={isLoading}
				on:click={handleStayOnCurrentHome}
			>
				{$format('button_stay_on_current_home', { default: 'Stay on current home' })}
			</Button>
			<Button
				variant="primary"
				responsiveSize={{ sm: 'm' }}
				class="width-md-m width-sm-full"
				loading={isLoading}
				on:click={handleSwitchToNewHome}
			>
				{$format('button_switch_to_new_home', { default: 'Switch to new home' })}
			</Button>
		{/if}
	</div>
</Modal>

<Modal
	showModal={showReceiveAccountDeletionWarning}
	on:close={closeReceiveAccountDeletionWarning}
	title={$format('settings_receive_account_deletion_title', {
		default: 'Delete source account?',
	})}
>
	<svelte:fragment slot="content">
		<Text class="mb-m">
			{@html $format('settings_receive_account_deletion_info', {
				default:
					"This is the only home on {email}. Receiving it will leave the owner's account with no homes and it will be permanently deleted. Do you want to proceed?",
				values: {
					home: highlightLabel(selectedReceiveUnitName),
					email: highlightLabel(sourceAccountEmail),
					sourceEmail: highlightLabel(sourceAccountEmail),
					currentEmail: highlightLabel(currentAccountEmail),
				},
			})}
		</Text>

		<Text class="mb-xs" styleType="body-small">
			{$format('settings_add_home_confirm_email_instruction', {
				default: 'To confirm, type the account email exactly as shown below:',
			})}
		</Text>
		<Text class="mb-m text-primary" styleType="body" breakWord>
			{sourceAccountEmail}
		</Text>

		{#if isV2}
			<InputV2
				id="confirm-deletion-email-input"
				label={$format('input_label_account_email_confirm', {
					default: 'Account email',
				})}
				aria-labelledby="confirm-deletion-email-input"
				bind:value={confirmDeletionEmailInput}
				error={confirmDeletionEmailError}
				type="text"
				inputmode="email"
				autocomplete="off"
			/>
		{:else}
			<Input
				id="confirm-deletion-email-input"
				label={$format('input_label_account_email_confirm', {
					default: 'Account email',
				})}
				aria-label={$format('input_label_account_email_confirm', {
					default: 'Account email',
				})}
				bind:value={confirmDeletionEmailInput}
				type="text"
				inputmode="email"
				autocomplete="off"
				error={confirmDeletionEmailError}
			/>
		{/if}
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		{#if isV2}
			<ButtonV2 variant="secondary" class="mr-s" on:click={closeReceiveAccountDeletionWarning}>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<ButtonV2
				variant="danger"
				loading={isLoading}
				disabled={!isDeletionConfirmEmailValid}
				on:click={handleReceiveAccountDeletionConfirmed}
			>
				{$format('button_add_home_and_delete_account', {
					default: 'Add home and delete account',
				})}
			</ButtonV2>
		{:else}
			<Button
				width="m"
				variant="secondary"
				class="mr-s"
				on:click={closeReceiveAccountDeletionWarning}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				width="m"
				variant="danger"
				loading={isLoading}
				disabled={!isDeletionConfirmEmailValid}
				on:click={handleReceiveAccountDeletionConfirmed}
			>
				{$format('button_add_home_and_delete_account', {
					default: 'Add home and delete account',
				})}
			</Button>
		{/if}
	</div>
</Modal>
